import type { HTMLProps, ReactNode } from 'react'
import { useState } from 'react'

import { useBoundStore } from '../../store/store'
import classNames from '../../utils/classNames'
import Icon from '../icon'
import { TailSpin } from '../loaders'

interface TextInputProps extends Omit<HTMLProps<HTMLInputElement>, 'size'> {
  suffix?: string
  isPassword?: boolean
  isValid?: boolean
  isReferral?: boolean
  isValidating?: boolean
  error?: string
  label?: string
  isDateInput?: boolean
  Badge?: ReactNode
  variant?: 'secondary'
  size?: 'small' | 'default'
}

const TextInput = ({
  suffix,
  isPassword,
  isValid,
  isReferral,
  isValidating,
  error,
  label,
  isDateInput,
  value,
  onChange,
  name,
  readOnly,
  Badge,
  variant,
  size = 'default',
  className,
  ...rest
}: TextInputProps) => {
  const theme = useBoundStore(state => state.common.theme)
  const [hide, setHide] = useState(true)

  const LABEL_SIZES = {
    small: 'text-xs xs:text-sm xl:text-base',
    default: 'text-sm xl:text-base',
  }

  return (
    <div className="realative w-full">
      {label && (
        <label
          className={classNames(
            'uppercase text-secondary-txt block mb-1',
            LABEL_SIZES[size],
          )}
          htmlFor={name}>
          {label}
        </label>
      )}
      <div
        className={classNames(
          'flex items-center relative px-3 py-0 rounded-md',
          isReferral ? 'bg-primary' : variant ? `bg-${variant}` : 'bg-primary',
          size === 'small' ? 'h-8 xs:h-9 xl:h-[38px]' : 'h-9 xl:h-[38px]',
          className,
        )}
        style={{ marginBottom: error ? 9 : size === 'small' ? 10 : 14 }}>
        {suffix && (
          <p
            className={classNames(
              'flex-none text-secondary-txt font-normal mr-2',
              LABEL_SIZES[size],
            )}>
            {suffix}
          </p>
        )}
        <input
          className={classNames(
            'placeholder-secondary-txt text-txt flex-1 w-full mr-3',
            LABEL_SIZES[size],
          )}
          type={isPassword ? (hide ? 'password' : 'text') : undefined}
          value={value}
          name={name}
          onChange={e => {
            if (!isDateInput) {
              return onChange?.(e)
            }

            const newValue = (e.target as HTMLInputElement).value
            const currentValue = (value ?? '') as string

            if (
              newValue.length > 10 ||
              (newValue && !/^\d+$/.test(newValue.replaceAll('/', '')))
            )
              return

            if (currentValue.length < newValue.length) {
              if (newValue.length === 2 || newValue.length === 5) {
                return onChange?.({
                  ...e,
                  target: {
                    ...e.target,
                    name,
                    value: newValue + '/',
                  } as EventTarget,
                })
              }
              if (newValue.length === 3 && newValue.charAt(2) !== '/') {
                return onChange?.({
                  ...e,
                  target: {
                    ...e.target,
                    name,
                    value: newValue.slice(0, 2) + '/' + newValue.slice(-1),
                  } as EventTarget,
                })
              }
              if (newValue.length === 6 && newValue.charAt(5) !== '/') {
                return onChange?.({
                  ...e,
                  target: {
                    ...e.target,
                    name,
                    value: newValue.slice(0, 5) + '/' + newValue.slice(-1),
                  } as EventTarget,
                })
              }
            }

            onChange?.(e)
          }}
          onKeyDown={e => {
            if (!isDateInput) return
            if (e.key === '/') {
              e.stopPropagation()
              e.preventDefault()
            }
          }}
          readOnly={readOnly}
          {...rest}
        />
        {isValidating ? (
          <TailSpin
            color={theme === 'dark' ? '#fff' : '#0E091E'}
            width={18}
            height={18}
          />
        ) : (
          isValid && <Icon id="check-circle" width="18" height="18" />
        )}
        {isPassword && (
          <button
            type="button"
            className="flex-none flx-ctr"
            onClick={() => setHide(prev => !prev)}>
            {hide ? (
              <Icon
                id="eye-slash-2"
                width={size === 'small' ? 15 : 18}
                height={size === 'small' ? 14 : 17}
                fill="#fff"
                className="fill-secondary-txt"
                aria-label="Hide Password"
              />
            ) : (
              <Icon
                id="eye-2"
                width={size === 'small' ? 15 : 18}
                height={size === 'small' ? 10 : 13}
                fill="white"
                className="fill-secondary-txt"
                aria-label="Show Password"
              />
            )}
          </button>
        )}
        {Badge}
      </div>
      {error && (
        <p
          className={classNames(
            'font-normal leading-4 text-[rgb(255,64,88)] mb-2.5',
            LABEL_SIZES[size],
          )}>
          {error}
        </p>
      )}
    </div>
  )
}

export default TextInput
